import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IconName } from '~/components';

import { productFruitsIds } from '~/constants/productFruits';
import { routes } from '~/providers/RouterProvider/router.routes';

export type MenuItem = {
  icon: IconName;
  label: string;
  productFruitsId?: string;
  subRoutes?: string[];
  to: string;
};

export type NavigationMenuItem = MenuItem & {
  group: 'invoicing' | 'community';
};

export const useNavigationMenuItems = () => {
  const { t } = useTranslation(['common']);

  return useMemo<NavigationMenuItem[]>(
    () => [
      // Dashboard
      {
        icon: 'Home',
        label: t('common:sidebar.dashboard'),
        to: routes.dashboard,
        group: 'invoicing',
        productFruitsId: productFruitsIds.navigation.dashboard,
      },

      // Quotations
      {
        icon: 'EditDocument',
        label: t('common:sidebar.quotations'),
        to: routes.quotations,
        group: 'invoicing',
        productFruitsId: productFruitsIds.navigation.quotations,
      },

      // Invoices and creditnotes
      {
        icon: 'RequestQuote',
        label: t('common:sidebar.invoices'),
        to: routes.invoices,
        subRoutes: [routes.creditNotes, routes.invoiceTemplates],
        group: 'invoicing',
        productFruitsId: productFruitsIds.navigation.invoices,
      },

      // Products
      {
        icon: 'Package',
        label: t('common:sidebar.products'),
        to: routes.products,
        group: 'invoicing',
        productFruitsId: productFruitsIds.navigation.products,
      },

      // Clients
      {
        icon: 'Group',
        label: t('common:sidebar.clients'),
        to: routes.clients,
        group: 'invoicing',
        productFruitsId: productFruitsIds.navigation.clients,
      },

      // Network
      {
        icon: 'Diversity',
        label: t('common:sidebar.community'),
        to: routes.network,
        group: 'community',
        productFruitsId: productFruitsIds.navigation.network,
      },

      // Forum
      {
        icon: 'Forum',
        label: t('common:sidebar.forum'),
        to: routes.forumTimeline,
        subRoutes: [routes.forumSelfPosts, routes.forumSavedPosts],
        group: 'community',
        productFruitsId: productFruitsIds.navigation.forum,
      },

      // Job posts
      {
        icon: 'Work',
        label: t('common:sidebar.jobPosts'),
        to: routes.jobPosts,
        subRoutes: [routes.bookmarkedJobPosts, routes.ownJobPosts],
        group: 'community',
        productFruitsId: productFruitsIds.navigation.jobPosts,
      },
    ],
    [t],
  );
};

export const useNavigationBarItems = () => {
  const { t } = useTranslation(['common']);

  return useMemo<MenuItem[]>(
    () => [
      {
        icon: 'Home',
        label: t('common:sidebar.dashboard'),
        to: routes.dashboard,
        productFruitsId: productFruitsIds.navigation.dashboard,
      },
      {
        icon: 'RequestQuote',
        label: t('common:sidebar.invoices'),
        to: routes.invoices,
        subRoutes: [routes.creditNotes, routes.invoiceTemplates],
        productFruitsId: productFruitsIds.navigation.invoices,
      },
      {
        icon: 'EditDocument',
        label: t('common:sidebar.quotations'),
        to: routes.quotations,
        productFruitsId: productFruitsIds.navigation.quotations,
      },
    ],
    [t],
  );
};

export const useMenuItems = () => {
  const { t } = useTranslation(['common']);

  return useMemo<MenuItem[]>(
    () => [
      // Invitations
      {
        icon: 'Mail',
        label: t('common:menu.invitations'),
        to: routes.invitations,
      },

      // Documents
      {
        icon: 'Folder',
        label: t('common:menu.documents'),
        to: routes.documents,
      },

      // Pricing
      {
        icon: 'Payments',
        label: t('common:menu.pricing'),
        to: routes.pricing,
      },

      // Settings
      {
        icon: 'Settings',
        label: t('common:menu.settings'),
        to: routes.settingsProfile,
      },
    ],
    [t],
  );
};
